import { useRequest } from "@prismicio/editor-support/Suspense"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  ErrorBoundary,
  Text
} from "@prismicio/editor-ui"
import { Suspense } from "react"

import { getPreviewUrls, type PreviewUrl } from "../../app/services/core"
import { useOpenRedirectUrl } from "../../document/LeftSidePanel/PreviewMenu"
import { useDocumentDataSearch } from "../DocumentListBody/useDocumentData"

interface ReleasePreviewProps {
  releaseId: string | undefined
  size?: "small" | "large"
}

export function ReleasePreview(props: ReleasePreviewProps) {
  const { releaseId, size = "large" } = props

  return (
    <ErrorBoundary
      renderError={() => <ReleasePreviewDropdownMenuFallback size={size} />}
    >
      <Suspense fallback={<ReleasePreviewDropdownMenuFallback size={size} />}>
        <ReleasePreviewDropdownMenu releaseId={releaseId} size={size} />
      </Suspense>
    </ErrorBoundary>
  )
}

function ReleasePreviewDropdownMenu(props: ReleasePreviewProps) {
  const { releaseId, size } = props

  const { results: previewOptions } = useRequest(getPreviewUrls, [])
  const { isRedirectUrlLoading, openRedirectUrl } = useOpenRedirectUrl()

  const result = useDocumentDataSearch({
    onCacheInvalidated: () => {},
    tab: "upcoming",
    releaseId
  })

  function openPreview(previewOption: PreviewUrl) {
    const firstDocument = result.documents[0]
    const firstDocumentVersion = firstDocument?.versions[0]

    if (!firstDocument || !firstDocumentVersion) return

    /**
     * To open the redirect url in a preview page we take the first document and version in the release
     * and open the redirect url for that document and version
     */
    openRedirectUrl({
      previewOption,
      documentMeta: firstDocument,
      documentVersion: firstDocumentVersion
    })
  }

  const hasPreviewOptions = previewOptions.length > 0

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button
          size={size}
          color="grey"
          startIcon="playCircle"
          endIcon="arrowDropDown"
          disabled={!releaseId}
          loading={isRedirectUrlLoading}
        >
          Preview
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" maxHeight={256} minWidth={256}>
        <DropdownMenuLabel>
          <Text color="grey11">Preview</Text>
        </DropdownMenuLabel>

        {hasPreviewOptions ? (
          previewOptions.map((previewOption) => (
            <DropdownMenuItem
              key={previewOption.id}
              onSelect={() => openPreview(previewOption)}
            >
              <Text href={previewOption.url} color="grey12">
                {previewOption.label}
              </Text>
            </DropdownMenuItem>
          ))
        ) : (
          <DropdownMenuItem>
            <Text color="grey11">No previews configured</Text>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface ReleasePreviewDropdownMenuFallbackProps {
  size?: "small" | "large"
}

function ReleasePreviewDropdownMenuFallback(
  props: ReleasePreviewDropdownMenuFallbackProps
) {
  const { size = "large" } = props

  return (
    <Button
      size={size}
      color="grey"
      startIcon="playCircle"
      endIcon="arrowDropDown"
      disabled
    >
      Preview
    </Button>
  )
}
